import React, { useState, useEffect, useMemo, createContext, useContext } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import '../../styles/Layout.css';
import api from '../../services/api';
import { ThemeContext } from '../../context/ThemeContext';
import userService from '../../services/userService';
import {
  AppBar,
  Box,
  Button,
  CssBaseline,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  TextField,
  Toolbar,
  Typography,
  Avatar,
  Menu,
  MenuItem,
  Tooltip,
  Popper,
  Paper,
  ClickAwayListener,
  MenuList,
  Checkbox } from
'@mui/material';

// Sidebar SVG icons as React components
import { ReactComponent as HomeIcon } from '../../assets/Icons/ico_home_wt.svg';
import { ReactComponent as ReportsIcon } from '../../assets/Icons/ico_reports_wt.svg';
import { ReactComponent as CalculationsIcon } from '../../assets/Icons/ico_calculations_wt.svg';
import { ReactComponent as AdjustmentsIcon } from '../../assets/Icons/ico_adjustments_wt.svg';
import { ReactComponent as EntitiesIcon } from '../../assets/Icons/ico_entities_wt.svg';
import { ReactComponent as StatesIcon } from '../../assets/Icons/ico_states_wt.svg';
import { ReactComponent as StateCalculationsIcon } from '../../assets/Icons/ico_statecalc_wt.svg';
import { ReactComponent as ScenariosIcon } from '../../assets/Icons/ico_scenarios_wt.svg';
import { ReactComponent as PitIcon } from '../../assets/Icons/ico_pit.svg';
import { ReactComponent as AccessIcon } from '../../assets/Icons/ico_access_wt.svg';
import AkrosLogo from '../AkrosLogo';
import {
  Menu as MenuIcon,
  Business as BusinessIcon,
  ManageSearch as AuditIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  ExpandMore as ExpandMoreIcon } from
'@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { usePointInTimeFilter } from '../../hooks/usePointInTimeFilter';
import { canUseInputs, canUseManage, canViewPlatformAdmin } from '../../config/siteConfig';

// Context for adjustment type filter
export const AdjustmentTypeContext = createContext();

export const useAdjustmentType = () => useContext(AdjustmentTypeContext);

// Context for scenarios
export const ScenarioContext = createContext();

export const useScenario = () => useContext(ScenarioContext);

// Context for entity filter
export const EntityFilterContext = createContext();

export const useEntityFilter = () => useContext(EntityFilterContext);

// Context for tag filter
export const TagFilterContext = createContext();

export const useTagFilter = () => useContext(TagFilterContext);

// Context for currency filter
export const CurrencyFilterContext = createContext();

export const useCurrencyFilter = () => useContext(CurrencyFilterContext);

// Context for entity group filter
export const EntityGroupFilterContext = createContext();

export const useEntityGroupFilter = () => useContext(EntityGroupFilterContext);
// Context for subtag filter
export const SubtagFilterContext = createContext();

export const useSubtagFilter = () => useContext(SubtagFilterContext);

// Context for prior scenario filter
export const PriorScenarioContext = createContext();

export const usePriorScenario = () => useContext(PriorScenarioContext);

// Context for state filter (state-calculations only)
export const StateFilterContext = createContext();

export const useStateFilter = () => useContext(StateFilterContext);

// Context for filing code filter (state-calculations only)
export const FilingCodeFilterContext = createContext();

export const useFilingCodeFilter = () => useContext(FilingCodeFilterContext);

// Context for calculation type (current-provision, deferred-rollforward, effective-tax-rate)
export const CalculationTypeContext = createContext();

export const useCalculationType = () => useContext(CalculationTypeContext);

const expandedDrawerWidth = 240;
const collapsedDrawerWidth = 50;

function Layout() {
  const { themeColor, setThemeColor } = useContext(ThemeContext);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [drawerExpanded, setDrawerExpanded] = useState(true);
  const [anchorEl, setAnchorEl] = useState(null);
  const [titleMenuAnchorEl, setTitleMenuAnchorEl] = useState(null);
  const [pointInTimeMenuAnchorEl, setPointInTimeMenuAnchorEl] = useState(null);
  const [adjustmentTypeMenuAnchorEl, setAdjustmentTypeMenuAnchorEl] = useState(null);
  const [scenarioMenuAnchorEl, setScenarioMenuAnchorEl] = useState(null);
  const [entityMenuAnchorEl, setEntityMenuAnchorEl] = useState(null);
  const [tagMenuAnchorEl, setTagMenuAnchorEl] = useState(null);
  const [currencyMenuAnchorEl, setCurrencyMenuAnchorEl] = useState(null);
  const [stateMenuAnchorEl, setStateMenuAnchorEl] = useState(null);
  const [filingCodeMenuAnchorEl, setFilingCodeMenuAnchorEl] = useState(null);
  const [selectedAdjustmentType, setSelectedAdjustmentType] = useState(() => {
    // Load from localStorage on initial render
    return localStorage.getItem('selectedAdjustmentType') || 'All';
  });
  const [selectedEntity, setSelectedEntity] = useState(() => {
    // Load from localStorage on initial render - stored as JSON array
    try {
      const stored = localStorage.getItem('selectedEntity');
      if (stored) return JSON.parse(stored);
    } catch {}
    return [];
  });
  const [selectedTag, setSelectedTag] = useState(() => {
    // Load from localStorage on initial render
    return localStorage.getItem('selectedTag') || 'All';
  });
  const [selectedCurrency, setSelectedCurrency] = useState(() => {
    // Load from localStorage on initial render
    return localStorage.getItem('selectedCurrency') || 'USD';
  });
  const [availableEntities, setAvailableEntities] = useState([]);
  const [availableTags, setAvailableTags] = useState([]);
  const [availableEntityGroups, setAvailableEntityGroups] = useState([]);
  const [allEntities, setAllEntities] = useState([]); // Store all entities for filtering
  const [allStateInputs, setAllStateInputs] = useState([]); // All state inputs for state/filing hierarchies
  const [availableStates, setAvailableStates] = useState(['All']);
  const [selectedState, setSelectedState] = useState(() => {
    try {const s = localStorage.getItem('selectedState');if (s) return JSON.parse(s);} catch {}
    return [];
  });
  const [availableFilingCodes, setAvailableFilingCodes] = useState([]);
  const [selectedFilingCode, setSelectedFilingCode] = useState(() => {
    try {const s = localStorage.getItem('selectedFilingCode');if (s) return JSON.parse(s);} catch {}
    return [];
  });
  const [availableSubtags, setAvailableSubtags] = useState([]);
  const [selectedSubtag, setSelectedSubtag] = useState(() => {
    // Load from localStorage on initial render
    return localStorage.getItem('selectedSubtag') || 'All';
  });
  const [subtagMenuAnchorEl, setSubtagMenuAnchorEl] = useState(null);
  const [priorScenarioMenuAnchorEl, setPriorScenarioMenuAnchorEl] = useState(null);
  const [selectedPriorScenario, setSelectedPriorScenario] = useState(null);
  const [priorScenarios, setPriorScenarios] = useState([]);
  const { availablePointInTimes, selectedPointInTime, setSelectedPointInTime, fetchAvailablePointInTimes } = usePointInTimeFilter();
  const [scenarios, setScenarios] = useState([]);
  const [selectedScenario, setSelectedScenario] = useState(null);
  const [scenariosLoading, setScenariosLoading] = useState(false);
  const [currentPageTitle, setCurrentPageTitle] = useState('Home');
  const navigate = useNavigate();
  const location = useLocation();
  const { user, site, logout } = useAuth();
  const [selectedTab, setSelectedTab] = useState(0);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [reportsCalculationType, setReportsCalculationType] = useState('current-provision');
  const [calculationsCalculationType, setCalculationsCalculationType] = useState('current-provision');
  const [stateCalculationsCalculationType, setStateCalculationsCalculationType] = useState('current-provision');

  // Define adjustment model types for the dropdown
  const adjustmentTypes = useMemo(() => [
  'All',
  'Pretax',
  'Payable',
  'Permanent',
  'Temporary',
  'GS Permanent',
  'GS Temporary',
  'TI Permanent',
  'TI Temporary',
  'Tax Adj. Provision',
  'Tax Adj. Return',
  'Tax Adj. RTP',
  'Net Operating Loss',
  'Tax Effected Temporary',
  'Valuation Allowance',
  'Tax Effected Valuation Allowance',
  'State Pre-App Perm',
  'State Pre-App Temp',
  'State Post-App Perm',
  'State Post-App Temp',
  'State Tax Effected Temp',
  'State Tax Adj. Return',
  'State Tax Adj. Provision',
  'State Tax Adj. RTP',
  'State Pre-App NOL',
  'State Post-App NOL',
  'State Pre-App Valuation Allowance',
  'State Post-App Valuation Allowance',
  'State Tax Effected Valuation Allowance'],
  []);

  const drawerWidth = drawerExpanded ? expandedDrawerWidth : collapsedDrawerWidth;

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const toggleDrawerExpanded = () => {
    setDrawerExpanded(!drawerExpanded);
  };

  const handleProfileMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };

  const handleSettingsOpen = () => {
    setSettingsOpen(true);
    handleProfileMenuClose();
  };

  const handleSettingsClose = () => {
    setSettingsOpen(false);
  };

  const handlePointInTimeMenuOpen = (event) => {
    setPointInTimeMenuAnchorEl(event.currentTarget);
  };

  const handlePointInTimeMenuClose = (pointInTime) => {
    setPointInTimeMenuAnchorEl(null);
    if (pointInTime) {
      setSelectedPointInTime(pointInTime);
    }
  };

  const handleAdjustmentTypeMenuOpen = (event) => {
    setAdjustmentTypeMenuAnchorEl(event.currentTarget);
  };

  const handleAdjustmentTypeMenuClose = (type) => {
    setAdjustmentTypeMenuAnchorEl(null);
    if (type) {
      setSelectedAdjustmentType(type);
    }
  };

  const handleScenarioMenuOpen = (event) => {
    setScenarioMenuAnchorEl(event.currentTarget);
  };

  const handleScenarioMenuClose = (scenario) => {
    setScenarioMenuAnchorEl(null);
    if (scenario) {
      setSelectedScenario(scenario);
    }
  };

  const handleEntityMenuOpen = (event) => {
    setEntityMenuAnchorEl(event.currentTarget);
  };

  const handleEntityMenuToggle = (entityItem) => {
    setSelectedEntity((prev) => {
      if (entityItem === 'All') return [];
      const next = prev.includes(entityItem) ?
      prev.filter((e) => e !== entityItem) :
      [...prev, entityItem];
      return next;
    });
  };

  const handleEntityMenuClose = () => {
    setEntityMenuAnchorEl(null);
  };

  const handleTagMenuOpen = (event) => {
    setTagMenuAnchorEl(event.currentTarget);
  };

  const handleTagMenuClose = (tag) => {
    setTagMenuAnchorEl(null);
    if (tag) {
      setSelectedTag(tag);
    }
  };

  const handleCurrencyMenuOpen = (event) => {
    setCurrencyMenuAnchorEl(event.currentTarget);
  };

  const handleCurrencyMenuClose = (currency) => {
    setCurrencyMenuAnchorEl(null);
    if (currency) {
      setSelectedCurrency(currency);
    }
  };

  const handleStateMenuOpen = (event) => {
    setStateMenuAnchorEl(event.currentTarget);
  };

  const handleStateMenuClose = () => {
    setStateMenuAnchorEl(null);
  };

  const handleStateMenuToggle = (stateItem) => {
    setSelectedState((prev) => {
      if (stateItem === 'All') {
        setSelectedFilingCode([]);
        return [];
      }
      const next = prev.includes(stateItem) ?
      prev.filter((s) => s !== stateItem) :
      [...prev, stateItem];
      // Reset filing codes no longer valid
      setSelectedFilingCode([]);
      return next;
    });
  };

  const handleFilingCodeMenuOpen = (event) => {
    setFilingCodeMenuAnchorEl(event.currentTarget);
  };

  const handleFilingCodeMenuClose = () => {
    setFilingCodeMenuAnchorEl(null);
  };

  const handleFilingCodeMenuToggle = (fc) => {
    setSelectedFilingCode((prev) => {
      if (fc === 'All') return [];
      return prev.includes(fc) ?
      prev.filter((f) => f !== fc) :
      [...prev, fc];
    });
  };

  const handleSubtagMenuOpen = (event) => {
    setSubtagMenuAnchorEl(event.currentTarget);
  };

  const handleSubtagMenuClose = (subtag) => {
    setSubtagMenuAnchorEl(null);
    if (subtag) {
      setSelectedSubtag(subtag);
    }
  };

  const handlePriorScenarioMenuOpen = (event) => {
    setPriorScenarioMenuAnchorEl(event.currentTarget);
  };

  const handlePriorScenarioMenuClose = (scenario) => {
    setPriorScenarioMenuAnchorEl(null);
    if (scenario) {
      setSelectedPriorScenario(scenario);
    }
  };

  // Fetch scenarios from API
  const fetchScenarios = async () => {
    setScenariosLoading(true);
    try {
      const response = await api.get('/master-scenarios');

      // Transform API data to match frontend schema if needed
      const transformedScenarios = (response.data.data || []).map((scenario) => ({
        id: scenario.id,
        scenarioCode: scenario.ScenarioCode,
        scenarioName: scenario.ScenarioName,
        year: scenario.Year,
        period: scenario.Period,
        periodDisplay: `${scenario.Year}-${scenario.Period}`,
        PointInTime: scenario.PointInTime, // Include PointInTime code for adjustment inputs
        entityList: scenario.entityList || [], // Include entityList for calculations page
        DomesticTaxRate: scenario.DomesticTaxRate, // Include domestic tax rate for calculations
        ModifiedBy: scenario.ModifiedBy,
        ModifiedOn: new Date(scenario.ModifiedOn).toLocaleString()
      }));

      setScenarios(transformedScenarios);

      // Set prior scenarios (excluding current scenario)
      setPriorScenarios(transformedScenarios);

      // Try to restore previously selected prior scenario from localStorage
      // (safe: if it no longer exists, keep it null)
      if (!selectedPriorScenario) {
        const savedPriorScenarioId = localStorage.getItem('selectedPriorScenarioId');
        if (savedPriorScenarioId) {
          const savedPriorScenario = transformedScenarios.find((s) => s.id === parseInt(savedPriorScenarioId));
          if (savedPriorScenario) {
            setSelectedPriorScenario(savedPriorScenario);
          }
        }
      }

      // Try to restore previously selected scenario from localStorage
      if (transformedScenarios.length > 0) {
        if (selectedScenario) {
          // Refresh the selected scenario with up-to-date data (e.g. entityList may have changed)
          const refreshed = transformedScenarios.find((s) => s.id === selectedScenario.id);
          if (refreshed) {
            setSelectedScenario(refreshed);
          }
        } else {
          const savedScenarioId = localStorage.getItem('selectedScenarioId');
          if (savedScenarioId) {
            const savedScenario = transformedScenarios.find((s) => s.id === parseInt(savedScenarioId));
            if (savedScenario) {
              setSelectedScenario(savedScenario);
            } else {
              setSelectedScenario(transformedScenarios[0]);
            }
          } else {
            setSelectedScenario(transformedScenarios[0]);
          }
        }
      }
    } catch (error) {
      console.error('Error fetching scenarios:', error);
    } finally {
      setScenariosLoading(false);
    }
  };

  // Fetch entities and entity groups from API
  const fetchEntities = async (currentScenario = null) => {
    try {
      const [entitiesResponse, groupsResponse] = await Promise.all([
      api.get('/entity-models/master'),
      api.get('/entity-groups')]
      );

      const entities = entitiesResponse.data.data || [];
      const groups = groupsResponse.data.data || [];

      // Use the provided scenario or the selected scenario
      const scenarioToUse = currentScenario || selectedScenario;

      let filteredEntityCodes;

      // Filter entities by scenario's entityList if a scenario is selected
      if (scenarioToUse && scenarioToUse.entityList && scenarioToUse.entityList.length > 0) {
        // Get entity codes that are in the scenario's entityList
        const scenarioEntityCodes = entities.
        filter((entity) => scenarioToUse.entityList.includes(entity.EntityCode)).
        map((entity) => entity.EntityCode);
        filteredEntityCodes = [...new Set(scenarioEntityCodes)].sort();
      } else {
        // If no scenario selected, show all entities
        filteredEntityCodes = [...new Set(entities.map((entity) => entity.EntityCode))].sort();
      }

      // Extract group names with prefix to distinguish them
      const groupNames = groups.map((group) => `[Group] ${group.name}`).sort();

      // Combine entities and groups
      const allEntitiesWithAll = ['All', ...groupNames, ...filteredEntityCodes];

      // Store all entities for filtering
      setAllEntities(entities);
      setAvailableEntities(allEntitiesWithAll);

      return allEntitiesWithAll; // Return the entities for validation
    } catch (error) {
      console.error('Error fetching entities:', error);
      return ['All']; // Return default on error
    }
  };

  // Fetch entity groups from API
  const fetchEntityGroups = async () => {
    try {
      const response = await api.get('/entity-groups');
      const groups = response.data.data || [];

      // Extract group names
      const groupNames = groups.map((group) => group.name).sort();
      const allGroups = ['All', ...groupNames];
      setAvailableEntityGroups(allGroups);

      return allGroups;
    } catch (error) {
      console.error('Error fetching entity groups:', error);
      return ['All'];
    }
  };

  // Filter entities based on selected group
  const filterEntitiesByGroup = async (groupName) => {
    try {
      // Fetch fresh group data to get entity list
      const groupsResponse = await api.get('/entity-groups');
      const groups = groupsResponse.data.data || [];
      const selectedGroup = groups.find((group) => group.name === groupName);

      if (selectedGroup && selectedGroup.entityList && selectedGroup.entityList.length > 0) {


        // Get entity codes for entities in this group
        const groupEntityCodes = [];

        // Match entity IDs from group to actual entities to get their codes
        selectedGroup.entityList.forEach((entityId) => {
          const entity = allEntities.find((e) => e.id === entityId);
          if (entity && entity.EntityCode) {
            groupEntityCodes.push(entity.EntityCode);
          }
        });



        if (groupEntityCodes.length > 0) {
          // Update available entities to show only group entities (but keep the combined list structure)
          const uniqueEntityCodes = [...new Set(allEntities.map((entity) => entity.EntityCode))].sort();
          const groupNames = groups.map((group) => `[Group] ${group.name}`).sort();
          const allEntitiesWithAll = ['All', ...groupNames, ...uniqueEntityCodes];
          setAvailableEntities(allEntitiesWithAll);
        }
      }
    } catch (error) {
      console.error('Error filtering entities by group:', error);
    }
  };

  // Fetch tags from adjustment APIs
  const fetchTags = async () => {
    try {
      // Import the adjustment type route map to get all endpoints
      const { adjustmentTypeToRouteMap } = await import('../../utils/adjustmentTypeUtils');

      // Get all adjustment types and their routes
      const adjustmentTypes = Object.keys(adjustmentTypeToRouteMap);
      const allPromises = [];

      // Fetch from each adjustment endpoint (same as TaxCalculations does)
      for (const type of adjustmentTypes) {
        const routePath = adjustmentTypeToRouteMap[type];
        if (routePath) {
          const promise = api.get(`/adjustments/${routePath}`).
          then((response) => {
            const data = response.data;
            return Array.isArray(data) ? data : data.data || [];
          }).
          catch((error) => {
            console.error(`Error fetching from /adjustments/${routePath}:`, error);
            return [];
          });
          allPromises.push(promise);
        }
      }

      const results = await Promise.all(allPromises);
      const allAdjustments = results.flatMap((result) => result);

      // Extract unique tags and filter out invalid entries
      const uniqueTags = [...new Set(allAdjustments.map((adj) => adj.Tag).filter((tag) => tag && tag !== 't'))].sort();
      const allTags = ['All', ...uniqueTags];
      setAvailableTags(allTags);

      return allTags;
    } catch (error) {
      console.error('Error fetching tags:', error);
      return ['All'];
    }
  };

  // Fetch subtags from adjustment APIs
  const fetchSubtags = async () => {
    try {
      // Import the adjustment type route map to get all endpoints
      const { adjustmentTypeToRouteMap } = await import('../../utils/adjustmentTypeUtils');

      // Get all adjustment types and their routes
      const adjustmentTypes = Object.keys(adjustmentTypeToRouteMap);
      const allPromises = [];

      // Fetch from each adjustment endpoint (same as TaxCalculations does)
      for (const type of adjustmentTypes) {
        const routePath = adjustmentTypeToRouteMap[type];
        if (routePath) {
          const promise = api.get(`/adjustments/${routePath}`).
          then((response) => {
            const data = response.data;
            return Array.isArray(data) ? data : data.data || [];
          }).
          catch((error) => {
            console.error(`Error fetching from /adjustments/${routePath}:`, error);
            return [];
          });
          allPromises.push(promise);
        }
      }

      const results = await Promise.all(allPromises);
      const allAdjustments = results.flatMap((result) => result);

      // Extract unique subtags and filter out invalid entries
      const uniqueSubtags = [...new Set(allAdjustments.map((adj) => adj.SubTag).filter((subtag) => subtag && subtag !== 'st'))].sort();
      const allSubtags = ['All', ...uniqueSubtags];
      setAvailableSubtags(allSubtags);

      return allSubtags;
    } catch (error) {
      console.error('Error fetching subtags:', error);
      return ['All'];
    }
  };

  const handleTitleMenuOpen = (event) => {
    // Show menu for entities/adjustments pages
    if (location.pathname === '/entities-management' || location.pathname === '/entity-groups-management') {
      setTitleMenuAnchorEl(event.currentTarget);
    }
  };

  const handleTitleMenuClose = () => {
    setTitleMenuAnchorEl(null);
  };

  const handleEntitiesTabChange = (route) => {
    handleTitleMenuClose();
    if (location.pathname !== route) {
      navigate(route);
    }
  };

  const handleLogout = () => {
    handleProfileMenuClose();
    logout();
    navigate('/login');
  };

  const calcTypeSubItems = [
  { value: 'current-provision', label: 'Current Provision' },
  { value: 'deferred-rollforward', label: 'Deferred Rollforward' },
  { value: 'effective-tax-rate', label: 'Effective Tax Rate' }];


  const menuItems = [
  { text: 'Home', icon: (isSelected) => <HomeIcon style={{ width: 20, height: 20, color: isSelected ? '#fff' : themeColor }} />, path: '/' },
  { text: 'Reports', icon: (isSelected) => <ReportsIcon style={{ width: 20, height: 20, color: isSelected ? '#fff' : themeColor }} />, path: '/reports' },
  { text: 'Calculations', icon: (isSelected) => <CalculationsIcon style={{ width: 20, height: 20, color: isSelected ? '#fff' : themeColor }} />, path: '/calculations' },
  { text: 'State Calculations', icon: (isSelected) => <StateCalculationsIcon style={{ width: 20, height: 20, color: isSelected ? '#fff' : themeColor }} />, path: '/state-calculations' }];


  // Inputs menu items
  const inputsItems = [
  { text: 'Adjustments', tooltipText: 'Input Adjustments', icon: (isSelected) => <AdjustmentsIcon style={{ width: 20, height: 20, color: isSelected ? '#fff' : themeColor }} />, path: '/adjustments-inputs' },
  { text: 'Entities', tooltipText: 'Input Entities', icon: (isSelected) => <EntitiesIcon style={{ width: 20, height: 20, color: isSelected ? '#fff' : themeColor }} />, path: '/entity-inputs' }, // Different route for inputs section
  { text: 'States', tooltipText: 'Input States', icon: (isSelected) => <StatesIcon style={{ width: 20, height: 20, color: isSelected ? '#fff' : themeColor }} />, path: '/states-inputs' }];


  // Management menu items 
  const visibleInputsItems = canUseInputs(user) ? inputsItems : [];

  const managementItems = !canUseManage(user) ? [] : [
  ...(canViewPlatformAdmin(user, site) ? [
  { text: 'Sites', tooltipText: 'Manage Sites', icon: (isSelected) => <BusinessIcon sx={{ width: 20, height: 20, color: isSelected ? '#fff' : themeColor }} />, path: '/sites' },
  { text: 'Audit', tooltipText: 'View Audit Logs', icon: (isSelected) => <AuditIcon sx={{ width: 20, height: 20, color: isSelected ? '#fff' : themeColor }} />, path: '/audit' }] :
  []),
  { text: 'Scenarios', tooltipText: 'Manage Scenarios', icon: (isSelected) => <ScenariosIcon style={{ width: 20, height: 20, color: isSelected ? '#fff' : themeColor }} />, path: '/scenarios-management' },
  { text: 'Adjustments', tooltipText: 'Manage Adjustments', icon: (isSelected) => <AdjustmentsIcon style={{ width: 20, height: 20, color: isSelected ? '#fff' : themeColor }} />, path: '/adjustments-management' },
  { text: 'Entities', tooltipText: 'Manage Entities', icon: (isSelected) => <EntitiesIcon style={{ width: 20, height: 20, color: isSelected ? '#fff' : themeColor }} />, path: '/entities-management' },
  { text: 'States', tooltipText: 'Manage States', icon: (isSelected) => <StatesIcon style={{ width: 20, height: 20, color: isSelected ? '#fff' : themeColor }} />, path: '/states-management' },
  { text: 'Point In Time', tooltipText: 'Manage Point In Time', icon: (isSelected) => <PitIcon style={{ width: 20, height: 20, color: isSelected ? '#fff' : themeColor }} />, path: '/point-in-time-management' },
  { text: 'Access', tooltipText: 'Manage Access', icon: (isSelected) => <AccessIcon style={{ width: 20, height: 20, color: isSelected ? '#fff' : themeColor }} />, path: '/access' }].
  map((item) => ({
    ...item,
    selected: location.pathname === item.path || item.path === '/entities-management' && location.pathname === '/entity-groups-management' || item.path === '/adjustments-management' && location.pathname === '/adjustment-rollups'
  }));

  // Fetch PointInTimes when on pages that need it
  useEffect(() => {
    const pointInTimePages = ['/scenarios-management', '/adjustments-management', '/states-management', '/entity-management'];
    if (pointInTimePages.includes(location.pathname)) {
      // Force refresh PointInTime data every time we navigate to these pages
      fetchAvailablePointInTimes();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // Save entity filter to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('selectedEntity', JSON.stringify(selectedEntity));
  }, [selectedEntity]);

  // Save adjustment type filter to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('selectedAdjustmentType', selectedAdjustmentType);
  }, [selectedAdjustmentType]);

  // Save tag filter to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('selectedTag', selectedTag);
  }, [selectedTag]);

  // Fetch master states to populate the State filter on /states-management
  const fetchMasterStatesForFilter = async () => {
    try {
      const response = await api.get('/states/master');
      const masterStates = response.data?.data || response.data || [];
      const uniqueStates = [...new Set(masterStates.map((s) => s.State).filter(Boolean))].sort();
      setAvailableStates(uniqueStates);
    } catch (error) {
      console.error('Error fetching master states for filter:', error);
    }
  };

  // Fetch all state inputs for filter hierarchy computation
  const fetchStateInputsForFilters = async () => {
    try {
      const response = await api.get('/states/input');
      const inputs = response.data?.data || response.data || [];
      setAllStateInputs(Array.isArray(inputs) ? inputs : []);
    } catch (error) {
      console.error('Error fetching state inputs for filters:', error);
    }
  };

  // Compute available states based on scenario + entity selection
  const computeAvailableStates = () => {
    let filtered = allStateInputs;
    if (selectedScenario && selectedScenario.scenarioCode !== 'All') {
      filtered = filtered.filter((si) => si.ScenarioCode === selectedScenario.scenarioCode);
    }
    const entityArr = Array.isArray(selectedEntity) ? selectedEntity : [];
    const plainEntities = entityArr.filter((e) => !e.startsWith('[Group] '));
    if (plainEntities.length > 0) {
      filtered = filtered.filter((si) => plainEntities.includes(si.EntityCode));
    }
    const uniqueStates = [...new Set(filtered.map((si) => si.StateCode).filter(Boolean))].sort();
    setAvailableStates(uniqueStates);
    // Remove any selected states no longer available
    const stateArr = Array.isArray(selectedState) ? selectedState : [];
    const validStates = stateArr.filter((s) => uniqueStates.includes(s));
    if (validStates.length !== stateArr.length) {
      setSelectedState(validStates);
      setSelectedFilingCode([]);
    }
  };

  // Compute available filing codes based on scenario + entity + state selection
  const computeAvailableFilingCodes = () => {
    let filtered = allStateInputs;
    if (selectedScenario && selectedScenario.scenarioCode !== 'All') {
      filtered = filtered.filter((si) => si.ScenarioCode === selectedScenario.scenarioCode);
    }
    const entityArr = Array.isArray(selectedEntity) ? selectedEntity : [];
    const plainEntities = entityArr.filter((e) => !e.startsWith('[Group] '));
    if (plainEntities.length > 0) {
      filtered = filtered.filter((si) => plainEntities.includes(si.EntityCode));
    }
    const stateArr = Array.isArray(selectedState) ? selectedState : [];
    if (stateArr.length > 0) {
      filtered = filtered.filter((si) => stateArr.includes(si.StateCode));
    }
    const uniqueFilingCodes = [...new Set(filtered.map((si) => si.FilingCode).filter(Boolean))].sort();
    setAvailableFilingCodes(uniqueFilingCodes);
    // Remove any selected filing codes no longer available
    const fcArr = Array.isArray(selectedFilingCode) ? selectedFilingCode : [];
    const validFcs = fcArr.filter((f) => uniqueFilingCodes.includes(f));
    if (validFcs.length !== fcArr.length) {
      setSelectedFilingCode(validFcs);
    }
  };

  // Save currency filter to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('selectedCurrency', selectedCurrency);
  }, [selectedCurrency]);

  // Save state filter to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('selectedState', JSON.stringify(selectedState));
  }, [selectedState]);

  // Save filing code filter to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('selectedFilingCode', JSON.stringify(selectedFilingCode));
  }, [selectedFilingCode]);

  // Save subtag filter to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('selectedSubtag', selectedSubtag);
  }, [selectedSubtag]);

  // Save selected scenario to localStorage whenever it changes
  useEffect(() => {
    if (selectedScenario) {
      localStorage.setItem('selectedScenarioId', selectedScenario.id.toString());
    }
  }, [selectedScenario]);

  // Save selected prior scenario to localStorage whenever it changes
  useEffect(() => {
    if (selectedPriorScenario?.id) {
      localStorage.setItem('selectedPriorScenarioId', selectedPriorScenario.id.toString());
    } else {
      localStorage.removeItem('selectedPriorScenarioId');
    }
  }, [selectedPriorScenario]);

  // Fetch entity groups on component mount
  useEffect(() => {
    fetchEntityGroups();
  }, []);

  // Fetch state inputs when on state-calculations page, or master states when on states-management
  useEffect(() => {
    if (location.pathname === '/state-calculations' || location.pathname === '/states-inputs') {
      fetchStateInputsForFilters();
    } else if (location.pathname === '/states-management') {
      fetchMasterStatesForFilter();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, selectedScenario]);

  // Recompute available states when scenario, entity, or allStateInputs change
  useEffect(() => {
    computeAvailableStates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allStateInputs, selectedScenario, selectedEntity]);

  // Recompute available filing codes when state or available states change
  useEffect(() => {
    computeAvailableFilingCodes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allStateInputs, selectedScenario, selectedEntity, selectedState]);

  // Update page title when location changes
  useEffect(() => {
    // Special case for management pages
    if (location.pathname === '/entities-management' || location.pathname === '/entity-groups-management' || location.pathname === '/states-management' || location.pathname === '/adjustments-management' || location.pathname === '/scenarios-management' || location.pathname === '/adjustment-rollups' || location.pathname === '/point-in-time-management') {

      setCurrentPageTitle('Manage');
      return;
    }

    // Special case for input pages
    if (location.pathname === '/adjustments-inputs' || location.pathname === '/entity-inputs' || location.pathname === '/states-inputs') {
      setCurrentPageTitle('Input');
      return;
    }

    // Set the page title based on the current path
    if (location.pathname === '/') {
      setCurrentPageTitle('Home');
    }
  }, [location.pathname]);

  const drawer =
  <div className="dark-sidebar" style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'auto' }}>
      <div>
        <Toolbar
        onClick={toggleDrawerExpanded}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center', // Added to vertically center content
          px: drawerExpanded ? 2 : 1,
          py: 2,
          cursor: 'pointer',
          '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.05)'
          }
        }}>

          {drawerExpanded ?
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
              <AkrosLogo
                iconColor={themeColor}
                nameColor="var(--color-text-light)"
                iconSize={30}
                height={26}
                sx={{ maxWidth: 180, overflow: 'hidden' }}
              />
              <ChevronLeftIcon sx={{ color: 'var(--color-text-light)', ml: 1 }} />
            </Box> :

        <>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <AkrosLogo
                  showName={false}
                  iconColor={themeColor}
                  iconSize={32}
                />
              </Box>
              {/* <ChevronRightIcon sx={{ color: 'var(--color-text-light)', mt: 1 }} /> */}
            </>
        }
        </Toolbar>
        <Divider sx={{ borderColor: 'rgba(210, 212, 212, 0.12)' }} />
        <List>
          {menuItems.map((item) => {
          const isActive = location.pathname === item.path;
          const hasSubItems = ['/reports', '/calculations', '/state-calculations'].includes(item.path);
          const getCalcType = () => {
            if (item.path === '/reports') return reportsCalculationType;
            if (item.path === '/calculations') return calculationsCalculationType;
            if (item.path === '/state-calculations') return stateCalculationsCalculationType;
            return null;
          };
          const setCalcType = (val) => {
            if (item.path === '/reports') setReportsCalculationType(val);else
            if (item.path === '/calculations') setCalculationsCalculationType(val);else
            if (item.path === '/state-calculations') setStateCalculationsCalculationType(val);
          };
          return (
            <React.Fragment key={item.text}>
                <ListItem disablePadding>
                  <Tooltip title={drawerExpanded ? '' : item.text} placement="right">
                    <ListItemButton
                    onClick={() => navigate(item.path)}
                    selected={isActive}
                    sx={{
                      px: drawerExpanded ? 2 : 'auto',
                      py: 0.6,
                      justifyContent: drawerExpanded ? 'initial' : 'center',
                      minHeight: '36px'
                    }}>

                      <ListItemIcon sx={{ minWidth: drawerExpanded ? 40 : 'auto', mr: drawerExpanded ? 0 : 'auto' }}>
                        {item.icon(isActive)}
                      </ListItemIcon>
                      {drawerExpanded && <ListItemText primary={item.text} />}
                      {drawerExpanded && hasSubItems && isActive &&
                    <ExpandMoreIcon sx={{ ml: 'auto', color: '#fff', fontSize: '1.2rem' }} />
                    }
                    </ListItemButton>
                  </Tooltip>
                </ListItem>
                {/* Sub-items for calculation type — only shown when page is active */}
                {isActive && hasSubItems && drawerExpanded && calcTypeSubItems.map((sub) =>
              <ListItem key={sub.value} disablePadding>
                    <ListItemButton
                  onClick={() => setCalcType(sub.value)}
                  sx={{
                    pl: 7,
                    py: 0.15,
                    minHeight: '24px',
                    '&:hover': { backgroundColor: 'rgba(255,255,255,0.08)' }
                  }}>

                      <ListItemText
                    primary={
                    <span style={{
                      fontSize: '0.78rem',
                      color: getCalcType() === sub.value ? themeColor : '#fff',
                      fontWeight: getCalcType() === sub.value ? 600 : 400
                    }}>
                            {sub.label}
                          </span>
                    } />

                    </ListItemButton>
                  </ListItem>
              )}
                {/* Collapsed icon-only tooltip sub-items */}
                {isActive && hasSubItems && !drawerExpanded && calcTypeSubItems.map((sub) =>
              <ListItem key={sub.value} disablePadding>
                    <Tooltip title={sub.label} placement="right">
                      <ListItemButton
                    onClick={() => setCalcType(sub.value)}
                    selected={getCalcType() === sub.value}
                    sx={{
                      px: 'auto',
                      py: 0.3,
                      justifyContent: 'center',
                      minHeight: '24px',
                      '&.Mui-selected': { backgroundColor: 'rgba(255,255,255,0.15)' }
                    }}>

                        <Box sx={{
                      width: 6, height: 6, borderRadius: '50%',
                      backgroundColor: getCalcType() === sub.value ? '#fff' : 'rgba(255,255,255,0.4)'
                    }} />
                      </ListItemButton>
                    </Tooltip>
                  </ListItem>
              )}
              </React.Fragment>);

        })}
        </List>

        {/* Inputs section */}
        {visibleInputsItems.length > 0 && <>
        <Box className={`section-header ${drawerExpanded ? 'expanded' : 'collapsed'}`}>
          {drawerExpanded ? 'INPUT' : 'I'}
        </Box>
        <List>
          {visibleInputsItems.map((item) =>
        <ListItem key={item.text} disablePadding>
              <Tooltip title={drawerExpanded ? '' : item.tooltipText || item.text} placement="right">
                <ListItemButton
              onClick={() => navigate(item.path)}
              selected={location.pathname === item.path}
              sx={{
                px: drawerExpanded ? 2 : 'auto',
                py: 0.6, // Reduced vertical padding to decrease height
                justifyContent: drawerExpanded ? 'initial' : 'center',
                minHeight: '36px' // Reduced from default 48px
              }}>

                  <ListItemIcon sx={{ minWidth: drawerExpanded ? 40 : 'auto', mr: drawerExpanded ? 0 : 'auto' }}>
                    {item.icon(location.pathname === item.path)}
                  </ListItemIcon>
                  {drawerExpanded && <ListItemText primary={item.text} />}
                </ListItemButton>
              </Tooltip>
            </ListItem>
        )}
        </List>
        </>}

        {/* Management section */}
        {managementItems.length > 0 && <>
        <Box className={`section-header ${drawerExpanded ? 'expanded' : 'collapsed'}`}>
          {drawerExpanded ? 'MANAGE' : 'M'}
        </Box>
        <List>
          {managementItems.map((item) =>
        <ListItem key={item.text} disablePadding>
              <Tooltip title={drawerExpanded ? '' : item.tooltipText || item.text} placement="right">
                <ListItemButton
              onClick={() => navigate(item.path)}
              selected={location.pathname === item.path || item.path === '/entities-management' && location.pathname === '/entity-groups-management' || item.path === '/adjustments-management' && location.pathname === '/adjustment-rollups'}
              sx={{
                px: drawerExpanded ? 2 : 'auto',
                py: 0.6, // Reduced vertical padding to decrease height
                justifyContent: drawerExpanded ? 'initial' : 'center',
                minHeight: '36px' // Reduced from default 48px
              }}>

                  <ListItemIcon sx={{ minWidth: drawerExpanded ? 40 : 'auto', mr: drawerExpanded ? 0 : 'auto' }}>
                    {item.icon(location.pathname === item.path || item.path === '/entities-management' && location.pathname === '/entity-groups-management' || item.path === '/adjustments-management' && location.pathname === '/adjustment-rollups')}
                  </ListItemIcon>
                  {drawerExpanded && <ListItemText primary={item.text} />}
                </ListItemButton>
              </Tooltip>
            </ListItem>
        )}
        </List>
        </>}
      </div>
      {/* Empty div to maintain structure - removed content moved above */}
      <div style={{ marginTop: 'auto' }}></div>
    </div>;


  // Fetch scenarios when component mounts or when navigating to adjustment inputs page
  useEffect(() => {
    if (location.pathname === '/' || location.pathname === '/adjustments-inputs' || location.pathname === '/entity-inputs' || location.pathname === '/states-inputs' || location.pathname === '/calculations' || location.pathname === '/state-calculations' || location.pathname === '/reports') {
      fetchScenarios();
    }
  }, [location.pathname, selectedPointInTime]);

  // Fetch entities when component mounts or when navigating to input pages
  useEffect(() => {
    if (location.pathname === '/' || location.pathname === '/adjustments-inputs' || location.pathname === '/entity-inputs' || location.pathname === '/states-inputs' || location.pathname === '/calculations' || location.pathname === '/state-calculations' || location.pathname === '/reports') {
      const loadEntitiesAndSetFilter = async () => {
        const entities = await fetchEntities();

        // Check for entity query parameter and set selected entity
        const searchParams = new URLSearchParams(location.search);
        const entityParam = searchParams.get('entity');
        if (entityParam && entities.includes(entityParam)) {
          // Only set if the entity exists in the available entities
          setSelectedEntity([entityParam]);
        } else {
          // Check if currently selected entity is still in the filtered list
          try {
            const stored = localStorage.getItem('selectedEntity');
            const currentEntity = stored ? JSON.parse(stored) : [];
            const invalid = Array.isArray(currentEntity) ?
            currentEntity.filter((e) => !entities.includes(e)) :
            [];
            if (invalid.length > 0) {
              setSelectedEntity(currentEntity.filter((e) => entities.includes(e)));
            }
          } catch {}
        }
        // If no entity parameter, keep the persisted value from localStorage (loaded in useState)
      };

      loadEntitiesAndSetFilter();
    }
  }, [location.pathname, location.search]);

  // Update entities when selected scenario changes
  useEffect(() => {
    if (selectedScenario && (location.pathname === '/' || location.pathname === '/adjustments-inputs' || location.pathname === '/entity-inputs' || location.pathname === '/states-inputs' || location.pathname === '/calculations' || location.pathname === '/state-calculations' || location.pathname === '/reports')) {
      const updateEntitiesForScenario = async () => {
        const entities = await fetchEntities(selectedScenario);

        // Check if currently selected entities are still valid for this scenario
        const invalidEntities = selectedEntity.filter((e) => !entities.includes(e));
        if (invalidEntities.length > 0) {
          setSelectedEntity(selectedEntity.filter((e) => entities.includes(e)));
        }
      };

      updateEntitiesForScenario();
    }
  }, [selectedScenario]);

  // Fetch tags when component mounts or when navigating to calculations page
  useEffect(() => {
    if (location.pathname === '/calculations' || location.pathname === '/state-calculations' || location.pathname === '/reports' || location.pathname === '/adjustments-inputs') {
      fetchTags();
    }
  }, [location.pathname]);

  // Fetch subtags when component mounts or when navigating to reports page
  useEffect(() => {
    if (location.pathname === '/reports' || location.pathname === '/adjustments-inputs') {
      fetchSubtags();
    }
  }, [location.pathname]);

  useEffect(() => {
    if (!selectedAdjustmentType && adjustmentTypes.length > 0) {
      setSelectedAdjustmentType(adjustmentTypes[0]);
    }

    // No longer auto-switching away from 'All' on adjustments-inputs page
    // allowing users to select 'All' to view all adjustment types
  }, [selectedAdjustmentType, adjustmentTypes]);

  // State and handlers for Adjustment Management dropdown
  const [adjustmentMenuAnchorEl, setAdjustmentMenuAnchorEl] = useState(null);
  const handleAdjustmentMenuOpen = (event) => {
    setAdjustmentMenuAnchorEl(event.currentTarget);
  };
  const handleAdjustmentMenuClose = () => {
    setAdjustmentMenuAnchorEl(null);
  };
  const handleAdjustmentTabChange = (route) => {
    handleAdjustmentMenuClose();
    if (location.pathname !== route) {
      navigate(route);
    }
  };

  // Context values
  const adjustmentTypeContextValue = {
    selectedAdjustmentType,
    setSelectedAdjustmentType,
    adjustmentTypes
  };

  const scenarioContextValue = {
    scenarios,
    selectedScenario,
    setSelectedScenario,
    fetchScenarios,
    scenariosLoading
  };

  const entityFilterContextValue = {
    availableEntities,
    selectedEntity,
    setSelectedEntity,
    fetchEntities
  };

  const tagFilterContextValue = {
    availableTags,
    selectedTag,
    setSelectedTag,
    fetchTags
  };

  const currencyFilterContextValue = {
    selectedCurrency,
    setSelectedCurrency
  };

  const subtagFilterContextValue = {
    availableSubtags,
    selectedSubtag,
    setSelectedSubtag,
    fetchSubtags
  };

  const entityGroupFilterContextValue = {
    availableEntityGroups,
    fetchEntityGroups,
    filterEntitiesByGroup
  };

  const priorScenarioContextValue = {
    priorScenarios,
    selectedPriorScenario,
    setSelectedPriorScenario
  };

  const calculationTypeContextValue = {
    reportsCalculationType,
    setReportsCalculationType,
    calculationsCalculationType,
    setCalculationsCalculationType,
    stateCalculationsCalculationType,
    setStateCalculationsCalculationType
  };

  const stateFilterContextValue = {
    availableStates,
    selectedState,
    setSelectedState
  };

  const filingCodeFilterContextValue = {
    availableFilingCodes,
    selectedFilingCode,
    setSelectedFilingCode
  };

  return (
    <StateFilterContext.Provider value={stateFilterContextValue}>
    <FilingCodeFilterContext.Provider value={filingCodeFilterContextValue}>
    <CalculationTypeContext.Provider value={calculationTypeContextValue}>
    <AdjustmentTypeContext.Provider value={adjustmentTypeContextValue}>
      <ScenarioContext.Provider value={scenarioContextValue}>
        <EntityFilterContext.Provider value={entityFilterContextValue}>
          <TagFilterContext.Provider value={tagFilterContextValue}>
            <CurrencyFilterContext.Provider value={currencyFilterContextValue}>
              <EntityGroupFilterContext.Provider value={entityGroupFilterContextValue}>
                <SubtagFilterContext.Provider value={subtagFilterContextValue}>
                  <PriorScenarioContext.Provider value={priorScenarioContextValue}>
        <Box sx={{ display: 'flex', minHeight: '100vh' }}>
          <CssBaseline />
          <AppBar
                              position="fixed"
                              sx={{
                                width: { sm: `calc(100% - ${drawerWidth}px)` },
                                ml: { sm: `${drawerWidth}px` },
                                backgroundColor: 'var(--color-primary)', // Changed to use theme variable
                                color: 'white', // Changed to white
                                boxShadow: '0px 2px 4px -1px rgba(0,0,0,0)',
                                height: '48px',
                                padding: 0,
                                '& .MuiToolbar-root': {
                                  paddingLeft: '20px',
                                  paddingRight: '8px'
                                },
                                transition: (theme) => theme.transitions.create(['margin', 'width'], {
                                  easing: theme.transitions.easing.sharp,
                                  duration: theme.transitions.duration.leavingScreen
                                })
                              }}>

            <Toolbar
                                variant="dense"
                                sx={{
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  minHeight: '48px !important', // Added minHeight property
                                  padding: '0 0px'
                                }}>

              <IconButton
                                  color="inherit"
                                  aria-label="open drawer"
                                  edge="start"
                                  onClick={handleDrawerToggle}
                                  sx={{ mr: 2, display: { sm: 'none' } }}>

                <MenuIcon />
              </IconButton>
              <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
                {['/', '/entities-management', '/states-management', '/adjustments-management', '/scenarios-management', '/adjustment-rollups', '/adjustments-inputs', '/entity-inputs', '/entity-groups-management', '/states-inputs', '/calculations', '/state-calculations', '/reports'].includes(location.pathname) ?
                                  <>
                    {location.pathname === '/entities-management' || location.pathname === '/entity-groups-management' ?
                                    <>
                        <Typography
                                        variant="h6"
                                        noWrap
                                        component="div"
                                        onClick={handleTitleMenuOpen}
                                        sx={{
                                          fontWeight: 'bold',
                                          cursor: 'pointer',
                                          display: 'flex',
                                          alignItems: 'center'
                                        }}>

                          {currentPageTitle} <img src="/assets/Icons/icon_dropdown_bw.png" alt="Dropdown" style={{ width: 16, height: 16, marginLeft: 16 }} />
                        </Typography>
                        <Menu
                                        anchorEl={titleMenuAnchorEl}
                                        open={Boolean(titleMenuAnchorEl)}
                                        onClose={handleTitleMenuClose}
                                        anchorOrigin={{
                                          vertical: 'bottom',
                                          horizontal: 'left'
                                        }}
                                        transformOrigin={{
                                          vertical: 'top',
                                          horizontal: 'left'
                                        }}>

                          <MenuItem
                                          selected={location.pathname === '/entities-management'}
                                          onClick={() => handleEntitiesTabChange('/entities-management')}>

                            Entities
                          </MenuItem>
                          <MenuItem
                                          selected={location.pathname === '/entity-groups-management'}
                                          onClick={() => handleEntitiesTabChange('/entity-groups-management')}>

                            Entity Groups
                          </MenuItem>
                        </Menu>
                      </> :
                                    location.pathname === '/adjustments-management' || location.pathname === '/adjustment-rollups' ?
                                    <>
                        <Typography
                                        variant="h6"
                                        noWrap
                                        component="div"
                                        onClick={handleAdjustmentMenuOpen}
                                        sx={{
                                          fontWeight: 'bold',
                                          cursor: 'pointer',
                                          display: 'flex',
                                          alignItems: 'center'
                                        }}>

                          {currentPageTitle}
                          <img src="/assets/Icons/icon_dropdown_bw.png" alt="Dropdown" style={{ width: 16, height: 16, marginLeft: 16 }} />
                        </Typography>
                        <Menu
                                        anchorEl={adjustmentMenuAnchorEl}
                                        open={Boolean(adjustmentMenuAnchorEl)}
                                        onClose={handleAdjustmentMenuClose}
                                        anchorOrigin={{
                                          vertical: 'bottom',
                                          horizontal: 'left'
                                        }}
                                        transformOrigin={{
                                          vertical: 'top',
                                          horizontal: 'left'
                                        }}>

                          <MenuItem
                                          selected={location.pathname === '/adjustments-management'}
                                          onClick={() => handleAdjustmentTabChange('/adjustments-management')}>

                            Adjustments
                          </MenuItem>
                          <MenuItem
                                          selected={location.pathname === '/adjustment-rollups'}
                                          onClick={() => handleAdjustmentTabChange('/adjustment-rollups')}>

                            Adjustment Rollups
                          </MenuItem>
                        </Menu>
                      </> :

                                    <Typography
                                      variant="h6"
                                      noWrap
                                      component="div"
                                      sx={{
                                        fontWeight: 'bold'
                                      }}>

                        {currentPageTitle}
                      </Typography>
                                    }

                    {/* Show PointInTime filter for entities and states pages, Scenario filter for adjustment inputs */}
                    <Divider orientation="vertical" sx={{ ml: 3, mr: 2, my: 1, backgroundColor: 'rgba(255, 255, 255, 0.8)', height: '24px', width: '2px' }} flexItem />

                    {location.pathname === '/' || location.pathname === '/adjustments-inputs' || location.pathname === '/entity-inputs' || location.pathname === '/states-inputs' || location.pathname === '/calculations' || location.pathname === '/state-calculations' || location.pathname === '/reports' ?
                                    // Scenario and Adjustment Type dropdowns for adjustments-inputs
                                    <>
                        <Typography
                                        variant="subtitle1"
                                        sx={{
                                          color: 'white',
                                          fontWeight: 'bold',
                                          mx: 1,
                                          textTransform: 'uppercase',
                                          fontSize: '0.875rem'
                                        }}>

                          Scenario
                        </Typography>
                        <Box
                                        onClick={handleScenarioMenuOpen}
                                        sx={{
                                          display: 'flex',
                                          alignItems: 'center',
                                          cursor: 'pointer',
                                          backgroundColor: 'white',
                                          borderRadius: 1,
                                          px: 1.5,
                                          py: 0.5,
                                          minWidth: '100px',
                                          maxWidth: '150px',
                                          '&:hover': { opacity: 0.9 }
                                        }}>

                          <Typography
                                          variant="subtitle1"
                                          sx={{
                                            color: 'var(--color-dark)',
                                            fontWeight: 'medium',
                                            fontSize: '0.875rem',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                            flex: 1
                                          }}>

                            {selectedScenario ? selectedScenario.scenarioName : 'Select Scenario'}
                          </Typography>
                          <KeyboardArrowDownIcon
                                          sx={{
                                            ml: 1,
                                            width: 20,
                                            height: 20,
                                            color: 'var(--color-primary)'
                                          }} />

                        </Box>
                        <Menu
                                        anchorEl={scenarioMenuAnchorEl}
                                        open={Boolean(scenarioMenuAnchorEl)}
                                        onClose={() => handleScenarioMenuClose()}
                                        anchorOrigin={{
                                          vertical: 'bottom',
                                          horizontal: 'left'
                                        }}
                                        transformOrigin={{
                                          vertical: 'top',
                                          horizontal: 'left'
                                        }}
                                        PaperProps={{
                                          style: {
                                            maxHeight: 300,
                                            overflowY: 'auto'
                                          }
                                        }}>

                          {scenarios.length > 0 ?
                                        scenarios.map((scenario) =>
                                        <MenuItem
                                          key={scenario.id}
                                          onClick={() => handleScenarioMenuClose(scenario)}
                                          selected={selectedScenario && selectedScenario.id === scenario.id}>

                                {scenario.scenarioName}
                              </MenuItem>
                                        ) :

                                        <MenuItem disabled>
                              <Typography variant="body2" color="text.secondary">
                                No scenarios available
                              </Typography>
                            </MenuItem>
                                        }
                        </Menu>
                        
                        {/* Prior Scenario Dropdown - Only for Home page */}
                        {location.pathname === '/' &&
                                      <>
                            <Divider orientation="vertical" sx={{ ml: 2, mr: 1.5, my: 1, backgroundColor: 'rgba(255, 255, 255, 0.8)', height: '24px', width: '2px' }} flexItem />
                            <Typography
                                          variant="subtitle1"
                                          sx={{
                                            color: 'white',
                                            fontWeight: 'bold',
                                            mx: 1,
                                            textTransform: 'uppercase',
                                            fontSize: '0.875rem'
                                          }}>

                              Prior Scenario
                            </Typography>
                            <Box
                                          onClick={handlePriorScenarioMenuOpen}
                                          sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            cursor: 'pointer',
                                            backgroundColor: 'white',
                                            borderRadius: 1,
                                            px: 1.5,
                                            py: 0.5,
                                            minWidth: '100px',
                                            maxWidth: '150px',
                                            '&:hover': { opacity: 0.9 }
                                          }}>

                              <Typography
                                            variant="subtitle1"
                                            sx={{
                                              color: 'var(--color-dark)',
                                              fontWeight: 'medium',
                                              fontSize: '0.875rem',
                                              overflow: 'hidden',
                                              textOverflow: 'ellipsis',
                                              whiteSpace: 'nowrap',
                                              flex: 1
                                            }}>

                                {selectedPriorScenario ? selectedPriorScenario.scenarioName : 'Select Prior'}
                              </Typography>
                              <KeyboardArrowDownIcon
                                            sx={{
                                              ml: 1,
                                              width: 20,
                                              height: 20,
                                              color: 'var(--color-primary)'
                                            }} />

                            </Box>
                            <Menu
                                          anchorEl={priorScenarioMenuAnchorEl}
                                          open={Boolean(priorScenarioMenuAnchorEl)}
                                          onClose={() => handlePriorScenarioMenuClose()}
                                          anchorOrigin={{
                                            vertical: 'bottom',
                                            horizontal: 'left'
                                          }}
                                          transformOrigin={{
                                            vertical: 'top',
                                            horizontal: 'left'
                                          }}
                                          PaperProps={{
                                            style: {
                                              maxHeight: 300,
                                              overflowY: 'auto'
                                            }
                                          }}>

                              <MenuItem
                                            onClick={() => handlePriorScenarioMenuClose(null)}
                                            selected={selectedPriorScenario === null}>

                                None
                              </MenuItem>
                              {priorScenarios.
                                          filter((scenario) => !selectedScenario || scenario.id !== selectedScenario.id).
                                          map((scenario) =>
                                          <MenuItem
                                            key={scenario.id}
                                            onClick={() => handlePriorScenarioMenuClose(scenario)}
                                            selected={selectedPriorScenario && selectedPriorScenario.id === scenario.id}>

                                  {scenario.scenarioName}
                                </MenuItem>
                                          )}
                            </Menu>
                          </>
                                      }
                        
                        {/* Entity Filter Dropdown for Input pages, Calculations, and Reports */}
                        {(location.pathname === '/adjustments-inputs' || location.pathname === '/entity-inputs' || location.pathname === '/states-inputs' || location.pathname === '/calculations' || location.pathname === '/state-calculations' || location.pathname === '/reports' || location.pathname === '/') &&
                                      <>
                          <Divider orientation="vertical" sx={{ ml: 2, mr: 1.5, my: 1, backgroundColor: 'rgba(255, 255, 255, 0.8)', height: '24px', width: '2px' }} flexItem />
                          <Typography
                                          variant="subtitle1"
                                          sx={{
                                            color: 'white',
                                            fontWeight: 'bold',
                                            mx: 0.5,
                                            textTransform: 'uppercase',
                                            fontSize: '0.75rem',
                                            minWidth: 'fit-content'
                                          }}>

                            Entity
                          </Typography>
                          <Box
                                          onClick={handleEntityMenuOpen}
                                          sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            cursor: 'pointer',
                                            backgroundColor: 'white',
                                            borderRadius: 1,
                                            px: 1.5,
                                            py: 0.5,
                                            minWidth: '80px',
                                            maxWidth: '120px',
                                            '&:hover': { opacity: 0.9 }
                                          }}>

                            <Typography
                                            variant="subtitle1"
                                            sx={{
                                              color: 'var(--color-dark)',
                                              fontWeight: 'medium',
                                              fontSize: '0.875rem',
                                              overflow: 'hidden',
                                              textOverflow: 'ellipsis',
                                              whiteSpace: 'nowrap',
                                              flex: 1
                                            }}>

                              {selectedEntity.length === 0 ? 'All' : selectedEntity.length === 1 ? selectedEntity[0] : `${selectedEntity.length} selected`}
                            </Typography>
                            <KeyboardArrowDownIcon
                                            sx={{
                                              ml: 1,
                                              width: 20,
                                              height: 20,
                                              color: 'var(--color-primary)'
                                            }} />

                          </Box>
                          <Menu
                                          anchorEl={entityMenuAnchorEl}
                                          open={Boolean(entityMenuAnchorEl)}
                                          onClose={handleEntityMenuClose}
                                          anchorOrigin={{
                                            vertical: 'bottom',
                                            horizontal: 'left'
                                          }}
                                          transformOrigin={{
                                            vertical: 'top',
                                            horizontal: 'left'
                                          }}
                                          PaperProps={{
                                            style: {
                                              maxHeight: 300,
                                              overflowY: 'auto'
                                            }
                                          }}>

                              <MenuItem
                                            key="All"
                                            onClick={() => handleEntityMenuToggle('All')}
                                            selected={selectedEntity.length === 0}>

                                <Checkbox checked={selectedEntity.length === 0} size="small" sx={{ p: 0, mr: 1 }} />
                                All
                              </MenuItem>
                            {availableEntities.filter((e) => e !== 'All').map((entityItem) =>
                                          <MenuItem
                                            key={entityItem}
                                            onClick={() => handleEntityMenuToggle(entityItem)}
                                            selected={selectedEntity.includes(entityItem)}
                                            sx={{
                                              fontWeight: entityItem.startsWith('[Group] ') ? 'bold' : 'normal',
                                              color: entityItem.startsWith('[Group] ') ? 'primary.main' : 'inherit'
                                            }}>

                                <Checkbox checked={selectedEntity.includes(entityItem)} size="small" sx={{ p: 0, mr: 1 }} />
                                {entityItem}
                              </MenuItem>
                                          )}
                          </Menu>
                        </>
                                      }
                        {/* Tag Filter Dropdown for Calculations, Reports, and Adjustment Inputs */}
                        {(location.pathname === '/calculations' || location.pathname === '/state-calculations' || location.pathname === '/reports' || location.pathname === '/adjustments-inputs') &&
                                      <>
                          <Divider orientation="vertical" sx={{ ml: 2, mr: 1.5, my: 1, backgroundColor: 'rgba(255, 255, 255, 0.8)', height: '24px', width: '2px' }} flexItem />
                          <Typography
                                          variant="subtitle1"
                                          sx={{
                                            color: 'white',
                                            fontWeight: 'bold',
                                            mx: 0.5,
                                            textTransform: 'uppercase',
                                            fontSize: '0.75rem',
                                            minWidth: 'fit-content'
                                          }}>

                            Tag
                          </Typography>
                          <Box
                                          onClick={handleTagMenuOpen}
                                          sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            cursor: 'pointer',
                                            backgroundColor: 'white',
                                            borderRadius: 1,
                                            px: 1.5,
                                            py: 0.5,
                                            minWidth: '70px',
                                            maxWidth: '100px',
                                            '&:hover': { opacity: 0.9 }
                                          }}>

                            <Typography
                                            variant="subtitle1"
                                            sx={{
                                              color: 'var(--color-dark)',
                                              fontWeight: 'medium',
                                              fontSize: '0.875rem',
                                              overflow: 'hidden',
                                              textOverflow: 'ellipsis',
                                              whiteSpace: 'nowrap',
                                              flex: 1
                                            }}>

                              {selectedTag}
                            </Typography>
                            <KeyboardArrowDownIcon
                                            sx={{
                                              ml: 1,
                                              width: 20,
                                              height: 20,
                                              color: 'var(--color-primary)'
                                            }} />

                          </Box>
                          <Menu
                                          anchorEl={tagMenuAnchorEl}
                                          open={Boolean(tagMenuAnchorEl)}
                                          onClose={() => handleTagMenuClose()}
                                          anchorOrigin={{
                                            vertical: 'bottom',
                                            horizontal: 'left'
                                          }}
                                          transformOrigin={{
                                            vertical: 'top',
                                            horizontal: 'left'
                                          }}
                                          PaperProps={{
                                            style: {
                                              maxHeight: 300,
                                              overflowY: 'auto'
                                            }
                                          }}>

                            {availableTags.map((tag) =>
                                          <MenuItem
                                            key={tag}
                                            onClick={() => handleTagMenuClose(tag)}
                                            selected={selectedTag === tag}>

                                {tag}
                              </MenuItem>
                                          )}
                          </Menu>
                        </>
                                      }
                        {/* Currency Filter Dropdown for Calculations and Reports */}
                        {(location.pathname === '/calculations' || location.pathname === '/state-calculations' || location.pathname === '/reports') &&
                                      <>
                          <Divider orientation="vertical" sx={{ ml: 2, mr: 1.5, my: 1, backgroundColor: 'rgba(255, 255, 255, 0.8)', height: '24px', width: '2px' }} flexItem />
                          <Typography
                                          variant="subtitle1"
                                          sx={{
                                            color: 'white',
                                            fontWeight: 'bold',
                                            mx: 0.5,
                                            textTransform: 'uppercase',
                                            fontSize: '0.75rem',
                                            minWidth: 'fit-content'
                                          }}>

                            Currency
                          </Typography>
                          <Box
                                          onClick={handleCurrencyMenuOpen}
                                          sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            cursor: 'pointer',
                                            backgroundColor: 'white',
                                            borderRadius: 1,
                                            px: 1.5,
                                            py: 0.5,
                                            minWidth: '70px',
                                            maxWidth: '100px',
                                            '&:hover': { opacity: 0.9 }
                                          }}>

                            <Typography
                                            variant="subtitle1"
                                            sx={{
                                              color: 'var(--color-dark)',
                                              fontWeight: 'medium',
                                              fontSize: '0.875rem',
                                              overflow: 'hidden',
                                              textOverflow: 'ellipsis',
                                              whiteSpace: 'nowrap',
                                              flex: 1
                                            }}>

                              {selectedCurrency}
                            </Typography>
                            <KeyboardArrowDownIcon
                                            sx={{
                                              ml: 1,
                                              width: 20,
                                              height: 20,
                                              color: 'var(--color-primary)'
                                            }} />

                          </Box>
                          <Menu
                                          anchorEl={currencyMenuAnchorEl}
                                          open={Boolean(currencyMenuAnchorEl)}
                                          onClose={() => handleCurrencyMenuClose()}
                                          anchorOrigin={{
                                            vertical: 'bottom',
                                            horizontal: 'left'
                                          }}
                                          transformOrigin={{
                                            vertical: 'top',
                                            horizontal: 'left'
                                          }}>

                            <MenuItem
                                            onClick={() => handleCurrencyMenuClose('USD')}
                                            selected={selectedCurrency === 'USD'}>

                              USD
                            </MenuItem>
                            <MenuItem
                                            onClick={() => handleCurrencyMenuClose('LC')}
                                            selected={selectedCurrency === 'LC'}>

                              LC
                            </MenuItem>
                          </Menu>
                        </>
                                      }
                        {/* State Filter Dropdown - state-calculations and states-inputs */}
                        {(location.pathname === '/state-calculations' || location.pathname === '/states-inputs') &&
                                      <>
                          <Divider orientation="vertical" sx={{ ml: 2, mr: 1.5, my: 1, backgroundColor: 'rgba(255, 255, 255, 0.8)', height: '24px', width: '2px' }} flexItem />
                          <Typography
                                          variant="subtitle1"
                                          sx={{
                                            color: 'white',
                                            fontWeight: 'bold',
                                            mx: 0.5,
                                            textTransform: 'uppercase',
                                            fontSize: '0.75rem',
                                            minWidth: 'fit-content'
                                          }}>

                            State
                          </Typography>
                          <Box
                                          onClick={handleStateMenuOpen}
                                          sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            cursor: 'pointer',
                                            backgroundColor: 'white',
                                            borderRadius: 1,
                                            px: 1.5,
                                            py: 0.5,
                                            minWidth: '60px',
                                            maxWidth: '100px',
                                            '&:hover': { opacity: 0.9 }
                                          }}>

                            <Typography
                                            variant="subtitle1"
                                            sx={{
                                              color: 'var(--color-dark)',
                                              fontWeight: 'medium',
                                              fontSize: '0.875rem',
                                              overflow: 'hidden',
                                              textOverflow: 'ellipsis',
                                              whiteSpace: 'nowrap',
                                              flex: 1
                                            }}>

                              {selectedState.length === 0 ? 'All' : selectedState.length === 1 ? selectedState[0] : `${selectedState.length} selected`}
                            </Typography>
                            <KeyboardArrowDownIcon sx={{ ml: 1, width: 20, height: 20, color: 'var(--color-primary)' }} />
                          </Box>
                          <Menu
                                          anchorEl={stateMenuAnchorEl}
                                          open={Boolean(stateMenuAnchorEl)}
                                          onClose={handleStateMenuClose}
                                          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                                          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                                          PaperProps={{ style: { maxHeight: 300, overflowY: 'auto' } }}>

                                <MenuItem
                                            key="All"
                                            onClick={() => handleStateMenuToggle('All')}
                                            selected={selectedState.length === 0}>

                                <Checkbox checked={selectedState.length === 0} size="small" sx={{ p: 0, mr: 1 }} />
                                All
                              </MenuItem>
                            {availableStates.map((state) =>
                                          <MenuItem
                                            key={state}
                                            onClick={() => handleStateMenuToggle(state)}
                                            selected={selectedState.includes(state)}>

                                <Checkbox checked={selectedState.includes(state)} size="small" sx={{ p: 0, mr: 1 }} />
                                {state}
                              </MenuItem>
                                          )}
                          </Menu>
                          </>
                                      }
                        {/* Filing Code Filter Dropdown - state-calculations and states-inputs */}
                        {(location.pathname === '/state-calculations' || location.pathname === '/states-inputs') &&
                                      <>
                          <Divider orientation="vertical" sx={{ ml: 2, mr: 1.5, my: 1, backgroundColor: 'rgba(255, 255, 255, 0.8)', height: '24px', width: '2px' }} flexItem />
                          <Typography
                                          variant="subtitle1"
                                          sx={{
                                            color: 'white',
                                            fontWeight: 'bold',
                                            mx: 0.5,
                                            textTransform: 'uppercase',
                                            fontSize: '0.75rem',
                                            minWidth: 'fit-content'
                                          }}>

                            Filing Code
                          </Typography>
                          <Box
                                          onClick={handleFilingCodeMenuOpen}
                                          sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            cursor: 'pointer',
                                            backgroundColor: 'white',
                                            borderRadius: 1,
                                            px: 1.5,
                                            py: 0.5,
                                            minWidth: '60px',
                                            maxWidth: '120px',
                                            '&:hover': { opacity: 0.9 }
                                          }}>

                            <Typography
                                            variant="subtitle1"
                                            sx={{
                                              color: 'var(--color-dark)',
                                              fontWeight: 'medium',
                                              fontSize: '0.875rem',
                                              overflow: 'hidden',
                                              textOverflow: 'ellipsis',
                                              whiteSpace: 'nowrap',
                                              flex: 1
                                            }}>

                              {selectedFilingCode.length === 0 ? 'All' : selectedFilingCode.length === 1 ? selectedFilingCode[0] : `${selectedFilingCode.length} selected`}
                            </Typography>
                            <KeyboardArrowDownIcon sx={{ ml: 1, width: 20, height: 20, color: 'var(--color-primary)' }} />
                          </Box>
                          <Menu
                                          anchorEl={filingCodeMenuAnchorEl}
                                          open={Boolean(filingCodeMenuAnchorEl)}
                                          onClose={handleFilingCodeMenuClose}
                                          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                                          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                                          PaperProps={{ style: { maxHeight: 300, overflowY: 'auto' } }}>

                                <MenuItem
                                            key="All"
                                            onClick={() => handleFilingCodeMenuToggle('All')}
                                            selected={selectedFilingCode.length === 0}>

                                <Checkbox checked={selectedFilingCode.length === 0} size="small" sx={{ p: 0, mr: 1 }} />
                                All
                              </MenuItem>
                            {availableFilingCodes.map((fc) =>
                                          <MenuItem
                                            key={fc}
                                            onClick={() => handleFilingCodeMenuToggle(fc)}
                                            selected={selectedFilingCode.includes(fc)}>

                                <Checkbox checked={selectedFilingCode.includes(fc)} size="small" sx={{ p: 0, mr: 1 }} />
                                {fc}
                              </MenuItem>
                                          )}
                          </Menu>
                          </>
                                      }
                        {/* Subtag Filter Dropdown for Reports and Adjustment Inputs */}
                        {(location.pathname === '/reports' || location.pathname === '/adjustments-inputs') &&
                                      <>
                          <Divider orientation="vertical" sx={{ ml: 3, mr: 2, my: 1, backgroundColor: 'rgba(255, 255, 255, 0.8)', height: '24px', width: '2px' }} flexItem />
                          <Typography
                                          variant="subtitle1"
                                          sx={{
                                            color: 'white',
                                            fontWeight: 'bold',
                                            mx: 1,
                                            textTransform: 'uppercase',
                                            fontSize: '0.875rem'
                                          }}>

                            Subtag
                          </Typography>
                          <Box
                                          onClick={handleSubtagMenuOpen}
                                          sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            cursor: 'pointer',
                                            backgroundColor: 'white',
                                            borderRadius: 1,
                                            px: 2,
                                            py: 0.5,
                                            '&:hover': { opacity: 0.9 }
                                          }}>

                            <Typography
                                            variant="subtitle1"
                                            sx={{
                                              color: 'var(--color-dark)',
                                              fontWeight: 'medium',
                                              fontSize: '0.875rem'
                                            }}>

                              {selectedSubtag}
                            </Typography>
                            <KeyboardArrowDownIcon
                                            sx={{
                                              ml: 1,
                                              width: 20,
                                              height: 20,
                                              color: 'var(--color-primary)'
                                            }} />

                          </Box>
                          <Menu
                                          anchorEl={subtagMenuAnchorEl}
                                          open={Boolean(subtagMenuAnchorEl)}
                                          onClose={() => handleSubtagMenuClose()}
                                          anchorOrigin={{
                                            vertical: 'bottom',
                                            horizontal: 'left'
                                          }}
                                          transformOrigin={{
                                            vertical: 'top',
                                            horizontal: 'left'
                                          }}
                                          PaperProps={{
                                            style: {
                                              maxHeight: 300,
                                              overflowY: 'auto'
                                            }
                                          }}>

                            {availableSubtags.map((subtag) =>
                                          <MenuItem
                                            key={subtag}
                                            onClick={() => handleSubtagMenuClose(subtag)}
                                            selected={selectedSubtag === subtag}>

                                {subtag}
                              </MenuItem>
                                          )}
                          </Menu>
                        </>
                                      }
                        {/* Adjustment Type Dropdown */}
                        {location.pathname === '/adjustments-inputs' &&
                                      <>
                            <Divider orientation="vertical" sx={{ ml: 3, mr: 2, my: 1, backgroundColor: 'rgba(255, 255, 255, 0.8)', height: '24px', width: '2px' }} flexItem />
                            <Typography
                                          variant="subtitle1"
                                          sx={{
                                            color: 'white',
                                            fontWeight: 'bold',
                                            mx: 1,
                                            textTransform: 'uppercase',
                                            fontSize: '0.875rem'
                                          }}>

                              Adjustment type
                            </Typography>
                            <Box
                                          onClick={handleAdjustmentTypeMenuOpen}
                                          sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            cursor: 'pointer',
                                            backgroundColor: 'white',
                                            borderRadius: 1,
                                            px: 2,
                                            py: 0.5,
                                            '&:hover': { opacity: 0.9 }
                                          }}>

                              <Typography
                                            variant="subtitle1"
                                            sx={{
                                              color: 'var(--color-dark)',
                                              fontWeight: 'medium',
                                              fontSize: '0.875rem'
                                            }}>

                                {selectedAdjustmentType}
                              </Typography>
                              <KeyboardArrowDownIcon
                                            sx={{
                                              ml: 1,
                                              width: 20,
                                              height: 20,
                                              color: 'var(--color-primary)'
                                            }} />

                            </Box>
                            <Menu
                                          anchorEl={adjustmentTypeMenuAnchorEl}
                                          open={Boolean(adjustmentTypeMenuAnchorEl)}
                                          onClose={() => handleAdjustmentTypeMenuClose()}
                                          anchorOrigin={{
                                            vertical: 'bottom',
                                            horizontal: 'left'
                                          }}
                                          transformOrigin={{
                                            vertical: 'top',
                                            horizontal: 'left'
                                          }}
                                          PaperProps={{
                                            style: {
                                              maxHeight: 300,
                                              overflowY: 'auto'
                                            }
                                          }}>

                              {adjustmentTypes.map((type) =>
                                          <MenuItem
                                            key={type}
                                            onClick={() => handleAdjustmentTypeMenuClose(type)}
                                            selected={selectedAdjustmentType === type}>

                                  {type}
                                </MenuItem>
                                          )}
                            </Menu>
                          </>
                                      }
                      </> : (

                                    /* PointInTime filter for other pages */
                                    <>
                        <Typography
                                        variant="subtitle1"
                                        sx={{
                                          color: 'white',
                                          fontWeight: 'bold',
                                          mx: 1,
                                          textTransform: 'uppercase',
                                          fontSize: '0.875rem'
                                        }}>

                          Point in Time
                        </Typography>

                        <Box
                                        onClick={handlePointInTimeMenuOpen}
                                        sx={{
                                          display: 'flex',
                                          alignItems: 'center',
                                          cursor: 'pointer',
                                          backgroundColor: 'white',
                                          borderRadius: 1,
                                          px: 2,
                                          py: 0.5,
                                          '&:hover': { opacity: 0.9 }
                                        }}>

                          <Typography
                                          variant="subtitle1"
                                          sx={{
                                            color: 'var(--color-dark)',
                                            fontWeight: 'medium',
                                            fontSize: '0.875rem'
                                          }}>

                            {selectedPointInTime === 'All' ? 'All' : availablePointInTimes.find((pit) => pit.code === selectedPointInTime)?.name || selectedPointInTime}
                          </Typography>
                          <KeyboardArrowDownIcon
                                          sx={{
                                            ml: 1,
                                            width: 20,
                                            height: 20,
                                            color: 'var(--color-primary)'
                                          }} />

                        </Box>

                        <Menu
                                        anchorEl={pointInTimeMenuAnchorEl}
                                        open={Boolean(pointInTimeMenuAnchorEl)}
                                        onClose={() => handlePointInTimeMenuClose()}
                                        anchorOrigin={{
                                          vertical: 'bottom',
                                          horizontal: 'left'
                                        }}
                                        transformOrigin={{
                                          vertical: 'top',
                                          horizontal: 'left'
                                        }}
                                        PaperProps={{
                                          style: {
                                            maxHeight: 300,
                                            overflowY: 'auto'
                                          }
                                        }}>

                          <MenuItem
                                          key="all-point-in-times"
                                          onClick={() => handlePointInTimeMenuClose('All')}
                                          selected={selectedPointInTime === 'All'}
                                          divider>

                            All
                          </MenuItem>
                          {availablePointInTimes.map((pit) =>
                                        <MenuItem
                                          key={pit.code}
                                          onClick={() => handlePointInTimeMenuClose(pit.code)}
                                          selected={selectedPointInTime === pit.code}>

                              {pit.name}
                            </MenuItem>
                                        )}
                        </Menu>
                      </>)
                                    }

                    {/* State Filter Dropdown - states-management */}
                    {location.pathname === '/states-management' &&
                                    <>
                        <Divider orientation="vertical" sx={{ ml: 2, mr: 1.5, my: 1, backgroundColor: 'rgba(255, 255, 255, 0.8)', height: '24px', width: '2px' }} flexItem />
                        <Typography
                                        variant="subtitle1"
                                        sx={{
                                          color: 'white',
                                          fontWeight: 'bold',
                                          mx: 0.5,
                                          textTransform: 'uppercase',
                                          fontSize: '0.75rem',
                                          minWidth: 'fit-content'
                                        }}>

                          State
                        </Typography>
                        <Box
                                        onClick={handleStateMenuOpen}
                                        sx={{
                                          display: 'flex',
                                          alignItems: 'center',
                                          cursor: 'pointer',
                                          backgroundColor: 'white',
                                          borderRadius: 1,
                                          px: 1.5,
                                          py: 0.5,
                                          minWidth: '60px',
                                          maxWidth: '100px',
                                          '&:hover': { opacity: 0.9 }
                                        }}>

                          <Typography
                                          variant="subtitle1"
                                          sx={{
                                            color: 'var(--color-dark)',
                                            fontWeight: 'medium',
                                            fontSize: '0.875rem',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                            flex: 1
                                          }}>

                            {selectedState.length === 0 ? 'All' : selectedState.length === 1 ? selectedState[0] : `${selectedState.length} selected`}
                          </Typography>
                          <KeyboardArrowDownIcon sx={{ ml: 1, width: 20, height: 20, color: 'var(--color-primary)' }} />
                        </Box>
                        <Menu
                                        anchorEl={stateMenuAnchorEl}
                                        open={Boolean(stateMenuAnchorEl)}
                                        onClose={handleStateMenuClose}
                                        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                                        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                                        PaperProps={{ style: { maxHeight: 300, overflowY: 'auto' } }}>

                          <MenuItem
                                          key="All"
                                          onClick={() => handleStateMenuToggle('All')}
                                          selected={selectedState.length === 0}>

                            <Checkbox checked={selectedState.length === 0} size="small" sx={{ p: 0, mr: 1 }} />
                            All
                          </MenuItem>
                          {availableStates.map((state) =>
                                        <MenuItem
                                          key={state}
                                          onClick={() => handleStateMenuToggle(state)}
                                          selected={selectedState.includes(state)}>

                              <Checkbox checked={selectedState.includes(state)} size="small" sx={{ p: 0, mr: 1 }} />
                              {state}
                            </MenuItem>
                                        )}
                        </Menu>
                      </>
                                    }

                    {/* Show Adjustment Type dropdown on adjustments management and rollups only (not on adjustments-inputs, since it is handled above) */}
                    {(location.pathname === '/adjustments-management' || location.pathname === '/adjustment-rollups') &&
                                    <>
                        <Divider orientation="vertical" sx={{ ml: 3, mr: 2, my: 1, backgroundColor: 'rgba(255, 255, 255, 0.8)', height: '24px', width: '2px' }} flexItem />

                        <Typography
                                        variant="subtitle1"
                                        sx={{
                                          color: 'white',
                                          fontWeight: 'bold',
                                          mx: 1,
                                          textTransform: 'uppercase',
                                          fontSize: '0.875rem'
                                        }}>

                          Adjustment type
                        </Typography>

                        <Box
                                        onClick={handleAdjustmentTypeMenuOpen}
                                        sx={{
                                          display: 'flex',
                                          alignItems: 'center',
                                          cursor: 'pointer',
                                          backgroundColor: 'white',
                                          borderRadius: 1,
                                          px: 2,
                                          py: 0.5,
                                          '&:hover': { opacity: 0.9 }
                                        }}>

                          <Typography
                                          variant="subtitle1"
                                          sx={{
                                            color: 'var(--color-dark)',
                                            fontWeight: 'medium',
                                            fontSize: '0.875rem'
                                          }}>

                            {selectedAdjustmentType}
                          </Typography>
                          <KeyboardArrowDownIcon
                                          sx={{
                                            ml: 1,
                                            width: 20,
                                            height: 20,
                                            color: 'var(--color-primary)'
                                          }} />

                        </Box>

                        <Menu
                                        anchorEl={adjustmentTypeMenuAnchorEl}
                                        open={Boolean(adjustmentTypeMenuAnchorEl)}
                                        onClose={() => handleAdjustmentTypeMenuClose()}
                                        anchorOrigin={{
                                          vertical: 'bottom',
                                          horizontal: 'left'
                                        }}
                                        transformOrigin={{
                                          vertical: 'top',
                                          horizontal: 'left'
                                        }}
                                        PaperProps={{
                                          style: {
                                            maxHeight: 300,
                                            overflowY: 'auto'
                                          }
                                        }}>

                          {adjustmentTypes.map((type) =>
                                        <MenuItem
                                          key={type}
                                          onClick={() => handleAdjustmentTypeMenuClose(type)}
                                          selected={selectedAdjustmentType === type}>

                              {type}
                            </MenuItem>
                                        )}
                        </Menu>
                      </>
                                    }
                  </> :

                                  <Typography
                                    variant="h6"
                                    noWrap
                                    component="div"
                                    sx={{
                                      fontWeight: 'bold'
                                    }}>

                    {currentPageTitle}
                  </Typography>
                                  }
              </Box>
              <IconButton
                                  size="large"
                                  edge="end"
                                  aria-label="account of current user"
                                  aria-haspopup="true"
                                  onClick={handleProfileMenuOpen}
                                  color="inherit"
                                  sx={{ mr: 0 }}>

                <Avatar sx={{
                                    width: 32,
                                    height: 32,
                                    fontSize: 16,
                                    bgcolor: 'var(--color-white)',
                                    color: 'var(--color-dark)'
                                  }}>
                  {user?.name?.charAt(0) || 'U'}
                </Avatar>
              </IconButton>
              <Menu
                                  anchorEl={anchorEl}
                                  open={Boolean(anchorEl)}
                                  onClose={handleProfileMenuClose}
                                  transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                                  anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}>

                <MenuItem onClick={handleSettingsOpen}>Settings</MenuItem>
                <MenuItem onClick={handleLogout}>Logout</MenuItem>
              </Menu>

              {/* Settings Dialog */}
              <Dialog open={settingsOpen} onClose={handleSettingsClose} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  Settings
                  <IconButton
                                      aria-label="close"
                                      onClick={handleSettingsClose}
                                      sx={{}}>

                    <span style={{ fontSize: 22, fontWeight: 'bold', lineHeight: 1 }}>&times;</span>
                  </IconButton>
                </DialogTitle>
                <DialogContent>
                  <Typography variant="h4" gutterBottom>Select your theme color</Typography>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
                    {[
                                      { name: 'Default', color: '#868686' },
                                      { name: 'Sea Green', color: '#469D8B' },
                                      { name: 'Tulip Tree', color: '#E8AE3C' },
                                      { name: 'Sunrise Orange', color: '#F57550' },
                                      { name: 'Blue Hosta', color: '#73BAD3' }].
                                      map(({ name, color }) =>
                                      <Box
                                        key={color}
                                        display="flex"
                                        flexDirection="column"
                                        alignItems="center"
                                        sx={{ cursor: 'pointer' }}
                                        onClick={async () => {
                                          setThemeColor(color);
                                          try {
                                            await userService.updateThemeColor(color);
                                          } catch (e) {
                                            console.error('Failed to update theme color', e);
                                          }
                                        }}>

                        <Box
                                          sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            width: 72,
                                            height: 72,
                                            borderRadius: '50%',
                                            mb: 1,
                                            transition: 'border 0.2s',
                                            border: themeColor === color ? `2px solid ${color}` : '4px solid transparent',
                                            '&:hover': {
                                              border: `2px solid ${color}`
                                            }
                                          }}>

                          <Box
                                            sx={{
                                              width: 60,
                                              height: 60,
                                              borderRadius: '50%',
                                              backgroundColor: color
                                            }} />

                        </Box>
                        <Typography
                                          variant="body2"
                                          align="center"
                                          sx={{ color: color, fontWeight: 'bold' }}>

                          {name}
                        </Typography>
                      </Box>
                                      )}
                  </Box>
                </DialogContent>

              </Dialog>
            </Toolbar>
          </AppBar>
          <Box
                              component="nav"
                              sx={{
                                width: { sm: drawerWidth },
                                flexShrink: { sm: 0 },
                                transition: (theme) => theme.transitions.create('width', {
                                  easing: theme.transitions.easing.sharp,
                                  duration: theme.transitions.duration.enteringScreen
                                })
                              }}>

            <Drawer
                                variant="temporary"
                                open={mobileOpen}
                                onClose={handleDrawerToggle}
                                ModalProps={{
                                  keepMounted: true // Better open performance on mobile.
                                }}
                                sx={{
                                  display: { xs: 'block', sm: 'none' },
                                  '& .MuiDrawer-paper': {
                                    boxSizing: 'border-box',
                                    width: drawerWidth,
                                    backgroundColor: 'var(--color-dark)',
                                    color: 'var(--color-text-light)',
                                    transition: (theme) => theme.transitions.create('width', {
                                      easing: theme.transitions.easing.sharp,
                                      duration: theme.transitions.duration.enteringScreen
                                    })
                                  }
                                }}>

              {drawer}
            </Drawer>
            <Drawer
                                variant="permanent"
                                className={drawerExpanded ? 'drawer-expanded' : 'drawer-collapsed'}
                                sx={{
                                  width: drawerWidth,
                                  flexShrink: 0,
                                  [`& .MuiDrawer-paper`]: {
                                    width: drawerWidth,
                                    boxSizing: 'border-box',
                                    borderRight: '0px !important',
                                    transition: (theme) => theme.transitions.create('width', {
                                      easing: theme.transitions.easing.sharp,
                                      duration: theme.transitions.duration.enteringScreen
                                    })
                                  }
                                }}
                                open>

              {drawer}
            </Drawer>
          </Box>
          <Box
                              component="main"
                              sx={{
                                flexGrow: 1,
                                px: 2,
                                pt: 0.5,
                                pb: 1,
                                width: { sm: `calc(100% - ${drawerWidth}px)` },
                                backgroundColor: 'var(--color-background-light)',
                                minHeight: '100vh',
                                transition: (theme) => theme.transitions.create('width', {
                                  easing: theme.transitions.easing.sharp,
                                  duration: theme.transitions.duration.enteringScreen
                                })
                              }}>

            <Toolbar />
            <Outlet />
          </Box>
        </Box>
                  </PriorScenarioContext.Provider>
                </SubtagFilterContext.Provider>
              </EntityGroupFilterContext.Provider>
            </CurrencyFilterContext.Provider>
          </TagFilterContext.Provider>
        </EntityFilterContext.Provider>
      </ScenarioContext.Provider>
    </AdjustmentTypeContext.Provider>
    </CalculationTypeContext.Provider>
    </FilingCodeFilterContext.Provider>
    </StateFilterContext.Provider>);

}

export default Layout;
